import {
  Button,
  Divider,
  FlexBox,
  InputText,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { AiDraftType } from 'lib-knowledge-management-common/graphql';
import { useState } from 'react';

import { UseSuggestionActions } from '../hooks/useSuggestionActions';
import { Suggestion } from '../utils/toSuggestion';

import s from './suggestionActions.module.scss';

/** Approving an update writes to the article; approving a new draft creates one. */
const APPROVE_LABEL: Record<AiDraftType, string> = {
  [AiDraftType.NewArticle]: 'Add to Drafts',
  [AiDraftType.ArticleUpdate]: 'Update article',
};

export const LOCKED_TOOLTIP =
  'This suggestion is being updated to match the article that was just published. Try again in a moment.';

type SuggestionActionsProps = {
  suggestion: Suggestion;
  actions: UseSuggestionActions;
  canImprove?: boolean;
};

/**
 * What the reviewer can do with the draft in front of them: ask the AI to
 * revise it, or take it out of the queue by discarding, saving or publishing.
 */
export function SuggestionActions({
  suggestion,
  actions,
  canImprove = true,
}: SuggestionActionsProps): JSX.Element {
  const [feedback, setFeedback] = useState('');
  const {
    discard,
    publish,
    approve,
    improve,
    isImproving,
    isRefiling,
    isSaving,
    pendingAction,
    canApprove,
    isLocked,
    approveBlockedReason,
  } = actions;

  /**
   * Only the button whose action is running says so, on habla's default
   * indicator. The other two are merely unavailable until it finishes, rather
   * than all three claiming to be working.
   *
   * Every button is disabled while any write is in flight, the running one
   * included: `isLoading` only styles the button — habla drops `onClick` on
   * `disabled` alone — so leaving it enabled let a focused button fire again on
   * Enter and submit the same approval twice. Disabled still renders the
   * indicator, so the running button keeps saying so.
   */
  const busy = (action: typeof pendingAction) => ({
    isLoading: pendingAction === action,
    disabled: isImproving || isRefiling || isSaving,
  });

  const lockedTooltip = (children: JSX.Element) => (
    <Tooltip content={LOCKED_TOOLTIP} disabled={!isLocked} wrap="div">
      {children}
    </Tooltip>
  );

  const onImprove = () => {
    if (!feedback.trim()) return;
    improve(feedback);
    // Cleared straight away: the mutation replaces the draft's body, so the
    // answer to the request is the article itself, not the request left behind.
    setFeedback('');
  };

  return (
    <FlexBox
      flexDirection="column"
      flex="0 0 auto"
      gap="300"
      padding="400 500"
      background="neutral.100"
      className={s.actions}
    >
      {canImprove && (
        <>
          <Text.Paragraph variant="1">Improve article</Text.Paragraph>

          {lockedTooltip(
            <InputText
              value={feedback}
              onChange={(changeEvent) => setFeedback(changeEvent.target.value)}
              onKeyDown={(keyEvent) => {
                if (keyEvent.key === 'Enter') onImprove();
                if (keyEvent.key === 'Escape') setFeedback('');
              }}
              placeholder="Ask AI to edit and improve the article ..."
              aria-label="Ask AI to edit and improve the article"
              disabled={isImproving || isRefiling || isSaving || isLocked}
              suffix={{
                type: 'button' as const,
                button: {
                  icon: 'enter' as const,
                  'aria-label': 'Send feedback to AI',
                  onClick: onImprove,
                  isLoading: isImproving,
                  // A revision and a re-file both write the whole draft, so the one
                  // that answers second wins; neither starts while the other runs.
                  disabled:
                    !feedback.trim() ||
                    isImproving ||
                    isRefiling ||
                    isSaving ||
                    isLocked,
                },
              }}
            />,
          )}

          <Divider />
        </>
      )}

      <FlexBox
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap="200"
      >
        <Button variant="tertiary" onClick={discard} {...busy('discard')}>
          Discard
        </Button>
        {lockedTooltip(
          <Button
            variant="secondary"
            onClick={publish}
            {...busy('publish')}
            disabled={!canApprove || busy('publish').disabled}
          >
            Publish
          </Button>,
        )}
        {lockedTooltip(
          <Button
            variant="primary"
            onClick={approve}
            {...busy('approve')}
            disabled={!canApprove || busy('approve').disabled}
          >
            {APPROVE_LABEL[suggestion.type]}
          </Button>,
        )}
      </FlexBox>

      {/* Stated rather than left to a disabled button: arriving without a
          category is the ordinary state of a new draft, not a rare one. */}
      {approveBlockedReason && (
        <Text.Paragraph variant="4" align="right" color="error" role="alert">
          {approveBlockedReason}
        </Text.Paragraph>
      )}
    </FlexBox>
  );
}
